/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FICHES CULTURALES - Composant React CRUD
 * ═══════════════════════════════════════════════════════════════════════════
 * Interface complète de gestion des fiches culturales :
 *  - Liste des fiches avec filtres
 *  - Création / Modification / Suppression
 *  - Workflow validation (Brouillon → Validé → Production)
 *  - Gestion des stades FAO-56
 *  - Gestion des actionneurs IoT
 *  - Gestion des FLAGS
 *
 * Papy 73+ / Joshua - Session 02/01/2026
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useCallback } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION API
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE = '/api/fiches';

// Helpers API
const api = {
  get: async (url) => {
    const res = await fetch(`${API_BASE}${url}`);
    return res.json();
  },
  post: async (url, data) => {
    const res = await fetch(`${API_BASE}${url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },
  put: async (url, data) => {
    const res = await fetch(`${API_BASE}${url}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    return res.json();
  },
  delete: async (url) => {
    const res = await fetch(`${API_BASE}${url}`, { method: 'DELETE' });
    return res.json();
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════════════════

const ICONES_CULTURES = ['🥕', '🍅', '🧅', '🥬', '🥒', '🌽', '🍆', '🫑', '🥦', '🌱', '🍓', '🍇'];
const COULEURS = ['#f97316', '#ef4444', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f59e0b'];

const TYPES_CULTURE = [
  { value: 'plein_champ', label: '🌾 Plein champ' },
  { value: 'sous_serre', label: '🏠 Sous serre' },
  { value: 'tunnel', label: '🎪 Tunnel' },
  { value: 'chassis', label: '📦 Châssis' }
];

const MODES_CULTURE = [
  { value: 'bio', label: '🌿 Bio' },
  { value: 'conventionnel', label: '🏭 Conventionnel' },
  { value: 'raisonne', label: '⚖️ Raisonné' }
];

const STADES_DEFAUT = [
  { code: 'INIT', nom: 'Germination', ordre: 1 },
  { code: 'DEV', nom: 'Développement', ordre: 2 },
  { code: 'MID', nom: 'Pleine croissance', ordre: 3 },
  { code: 'FIN', nom: 'Maturation', ordre: 4 }
];

const FLAGS_DISPONIBLES = [
  { code: 'ARR', nom: 'Arrosage', icone: '💧', description: 'Déclenche irrigation si humidité basse' },
  { code: 'GEL', nom: 'Gel', icone: '🥶', description: 'Protection antigel' },
  { code: 'SEC', nom: 'Sécheresse', icone: '🏜️', description: 'Alerte sécheresse critique' },
  { code: 'EAU', nom: 'Excès eau', icone: '💦', description: 'Excès humidité' },
  { code: 'TMP', nom: 'Température', icone: '🌡️', description: 'Stress thermique' },
  { code: 'REC', nom: 'Récolte', icone: '📅', description: 'Récolte proche' },
  { code: 'ECL', nom: 'Éclaircissage', icone: '✂️', description: 'Éclaircissage nécessaire' },
  { code: 'LUM', nom: 'Lumière', icone: '💡', description: 'Manque de lumière' }
];

const ACTIONNEURS_TYPES = [
  { code: 'vanne', nom: 'Vanne Irrigation', icone: '🚿' },
  { code: 'voile', nom: 'Voile P17', icone: '🛡️' },
  { code: 'ventilation', nom: 'Ventilation', icone: '🌀' },
  { code: 'chauffage', nom: 'Chauffage', icone: '🔥' },
  { code: 'brumisation', nom: 'Brumisation', icone: '💨' },
  { code: 'brasseur', nom: 'Brasseur Air', icone: '🔄' },
  { code: 'eclairage', nom: 'Éclairage', icone: '💡' },
  { code: 'ombrage', nom: 'Ombrage', icone: '🌥️' }
];

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANTS UI
// ═══════════════════════════════════════════════════════════════════════════

// Badge Statut
const StatutBadge = ({ statut }) => {
  const config = {
    brouillon: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', icon: '📝' },
    valide: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', icon: '✅' },
    production: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', icon: '🚀' },
    archive: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-300', icon: '📦' }
  };
  const c = config[statut] || config.brouillon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${c.bg} ${c.text} ${c.border}`}>
      {c.icon} {statut}
    </span>
  );
};

// Toast notification
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bg = type === 'error' ? 'bg-red-500' : type === 'success' ? 'bg-green-500' : 'bg-blue-500';
  
  return (
    <div className={`fixed top-4 right-4 ${bg} text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2`}>
      {type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️'} {message}
      <button onClick={onClose} className="ml-2 hover:opacity-75">×</button>
    </div>
  );
};

// Modal
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  
  const sizeClass = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  }[size];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-40 p-4">
      <div className={`bg-white rounded-xl shadow-2xl w-full ${sizeClass} max-h-[90vh] overflow-hidden flex flex-col`}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {children}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// FORMULAIRE FICHE
// ═══════════════════════════════════════════════════════════════════════════

const FicheForm = ({ fiche, onSave, onCancel }) => {
  const [form, setForm] = useState({
    code: '',
    nom: '',
    nom_court: '',
    famille: '',
    icone: '🌱',
    couleur: '#22c55e',
    type_culture: 'plein_champ',
    mode_culture: 'bio',
    region_reference: 'Normandie',
    jours_cycle_type: 90,
    jours_cycle_min: null,
    jours_cycle_max: null,
    semis_debut: '',
    semis_fin: '',
    recolte_debut: '',
    recolte_fin: '',
    temp_germination_min: null,
    temp_germination_opt: null,
    temp_germination_max: null,
    etc_total_type_mm: null,
    resist_gel: 'non',
    resist_secheresse: 'moyenne',
    resist_chaleur: 'moyenne',
    source_donnees: 'FAO-56',
    notes: '',
    ...fiche
  });

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'number' ? (value ? parseFloat(value) : null) : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const isEdit = !!fiche?.id;
  const isLocked = fiche?.statut === 'production';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {isLocked && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-800 text-sm">
          🔒 Fiche en production - Lecture seule. Utilisez "Dupliquer" pour créer une nouvelle version.
        </div>
      )}

      {/* Identification */}
      <fieldset className="border rounded-lg p-4">
        <legend className="text-sm font-medium px-2">Identification</legend>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
            <input
              type="text"
              name="code"
              value={form.code}
              onChange={handleChange}
              disabled={isEdit}
              required
              placeholder="CAROTTE-NANTAISE"
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
            <input
              type="text"
              name="nom"
              value={form.nom}
              onChange={handleChange}
              disabled={isLocked}
              required
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Famille</label>
            <input
              type="text"
              name="famille"
              value={form.famille}
              onChange={handleChange}
              disabled={isLocked}
              placeholder="Apiacées"
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Icône</label>
            <div className="flex flex-wrap gap-1">
              {ICONES_CULTURES.map(icon => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => !isLocked && setForm(p => ({ ...p, icone: icon }))}
                  className={`text-2xl p-1 rounded ${form.icone === icon ? 'bg-blue-100 ring-2 ring-blue-500' : 'hover:bg-gray-100'}`}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Couleur</label>
            <div className="flex flex-wrap gap-1">
              {COULEURS.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => !isLocked && setForm(p => ({ ...p, couleur: color }))}
                  className={`w-8 h-8 rounded-full ${form.couleur === color ? 'ring-2 ring-offset-2 ring-gray-400' : ''}`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type culture</label>
            <select
              name="type_culture"
              value={form.type_culture}
              onChange={handleChange}
              disabled={isLocked}
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            >
              {TYPES_CULTURE.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
            <select
              name="mode_culture"
              value={form.mode_culture}
              onChange={handleChange}
              disabled={isLocked}
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            >
              {MODES_CULTURE.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>
      </fieldset>

      {/* Cycle cultural */}
      <fieldset className="border rounded-lg p-4">
        <legend className="text-sm font-medium px-2">Cycle Cultural</legend>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Durée (jours) *</label>
            <input
              type="number"
              name="jours_cycle_type"
              value={form.jours_cycle_type || ''}
              onChange={handleChange}
              disabled={isLocked}
              required
              min="1"
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min</label>
            <input
              type="number"
              name="jours_cycle_min"
              value={form.jours_cycle_min || ''}
              onChange={handleChange}
              disabled={isLocked}
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max</label>
            <input
              type="number"
              name="jours_cycle_max"
              value={form.jours_cycle_max || ''}
              onChange={handleChange}
              disabled={isLocked}
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Semis début</label>
            <input
              type="text"
              name="semis_debut"
              value={form.semis_debut || ''}
              onChange={handleChange}
              disabled={isLocked}
              placeholder="03-15"
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Semis fin</label>
            <input
              type="text"
              name="semis_fin"
              value={form.semis_fin || ''}
              onChange={handleChange}
              disabled={isLocked}
              placeholder="07-15"
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ETc total (mm)</label>
            <input
              type="number"
              name="etc_total_type_mm"
              value={form.etc_total_type_mm || ''}
              onChange={handleChange}
              disabled={isLocked}
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
        </div>
      </fieldset>

      {/* Germination */}
      <fieldset className="border rounded-lg p-4">
        <legend className="text-sm font-medium px-2">Germination</legend>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temp. min (°C)</label>
            <input
              type="number"
              name="temp_germination_min"
              value={form.temp_germination_min || ''}
              onChange={handleChange}
              disabled={isLocked}
              step="0.1"
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temp. optimale (°C)</label>
            <input
              type="number"
              name="temp_germination_opt"
              value={form.temp_germination_opt || ''}
              onChange={handleChange}
              disabled={isLocked}
              step="0.1"
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Temp. max (°C)</label>
            <input
              type="number"
              name="temp_germination_max"
              value={form.temp_germination_max || ''}
              onChange={handleChange}
              disabled={isLocked}
              step="0.1"
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
          </div>
        </div>
      </fieldset>

      {/* Résistances */}
      <fieldset className="border rounded-lg p-4">
        <legend className="text-sm font-medium px-2">Résistances</legend>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Gel</label>
            <select name="resist_gel" value={form.resist_gel} onChange={handleChange} disabled={isLocked}
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100">
              <option value="non">Non</option>
              <option value="partiel">Partiel</option>
              <option value="oui">Oui</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sécheresse</label>
            <select name="resist_secheresse" value={form.resist_secheresse} onChange={handleChange} disabled={isLocked}
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100">
              <option value="faible">Faible</option>
              <option value="moyenne">Moyenne</option>
              <option value="bonne">Bonne</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Chaleur</label>
            <select name="resist_chaleur" value={form.resist_chaleur} onChange={handleChange} disabled={isLocked}
              className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100">
              <option value="faible">Faible</option>
              <option value="moyenne">Moyenne</option>
              <option value="bonne">Bonne</option>
            </select>
          </div>
        </div>
      </fieldset>

      {/* Notes */}
      <fieldset className="border rounded-lg p-4">
        <legend className="text-sm font-medium px-2">Notes</legend>
        <textarea
          name="notes"
          value={form.notes || ''}
          onChange={handleChange}
          disabled={isLocked}
          rows="3"
          className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
          placeholder="Notes, observations, conseils..."
        />
      </fieldset>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border rounded-lg hover:bg-gray-50"
        >
          Annuler
        </button>
        {!isLocked && (
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            {isEdit ? 'Enregistrer' : 'Créer la fiche'}
          </button>
        )}
      </div>
    </form>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// ÉDITEUR DE STADES
// ═══════════════════════════════════════════════════════════════════════════

const StadesEditor = ({ ficheId, stades: initialStades, isLocked, onUpdate }) => {
  const [stades, setStades] = useState(initialStades || []);
  const [editingId, setEditingId] = useState(null);

  const handleAddStade = async () => {
    const newStade = {
      code_stade: `STADE${stades.length + 1}`,
      nom: 'Nouveau stade',
      ordre: stades.length + 1,
      duree_jours_type: 20,
      kc: 0.80,
      sensibilite_stress: 'moyenne',
      seuil_humidite_min: 25,
      seuil_humidite_opt: 40,
      seuil_humidite_max: 55,
      seuil_temp_sol_max: 22
    };
    
    const result = await api.post(`/${ficheId}/stades`, newStade);
    if (result.success) {
      setStades([...stades, { ...newStade, id: result.data.id }]);
      onUpdate?.();
    }
  };

  const handleUpdateStade = async (stadeId, field, value) => {
    const result = await api.put(`/${ficheId}/stades/${stadeId}`, { [field]: value });
    if (result.success) {
      setStades(stades.map(s => s.id === stadeId ? { ...s, [field]: value } : s));
    }
  };

  const handleDeleteStade = async (stadeId) => {
    if (!confirm('Supprimer ce stade ?')) return;
    const result = await api.delete(`/${ficheId}/stades/${stadeId}`);
    if (result.success) {
      setStades(stades.filter(s => s.id !== stadeId));
      onUpdate?.();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="font-medium">Stades culturaux FAO-56</h3>
        {!isLocked && (
          <button onClick={handleAddStade} className="px-3 py-1 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600">
            + Ajouter stade
          </button>
        )}
      </div>

      {stades.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Aucun stade défini. Cliquez sur "+ Ajouter stade" pour commencer.
        </div>
      ) : (
        <div className="space-y-3">
          {stades.map((stade) => (
            <div key={stade.id} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🌱</span>
                  <div>
                    <input
                      type="text"
                      value={stade.code_stade}
                      onChange={(e) => handleUpdateStade(stade.id, 'code_stade', e.target.value)}
                      disabled={isLocked}
                      className="font-bold text-lg bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none disabled:text-gray-600"
                    />
                    <input
                      type="text"
                      value={stade.nom}
                      onChange={(e) => handleUpdateStade(stade.id, 'nom', e.target.value)}
                      disabled={isLocked}
                      className="block text-sm text-gray-600 bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
                {!isLocked && (
                  <button onClick={() => handleDeleteStade(stade.id)} className="text-red-500 hover:text-red-700">
                    🗑️
                  </button>
                )}
              </div>

              <div className="grid grid-cols-4 md:grid-cols-8 gap-3 text-sm">
                <div>
                  <label className="block text-xs text-gray-500">Durée (j)</label>
                  <input type="number" value={stade.duree_jours_type || ''} disabled={isLocked}
                    onChange={(e) => handleUpdateStade(stade.id, 'duree_jours_type', parseInt(e.target.value))}
                    className="w-full border rounded px-2 py-1 text-sm disabled:bg-gray-200" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">Kc</label>
                  <input type="number" step="0.01" value={stade.kc || ''} disabled={isLocked}
                    onChange={(e) => handleUpdateStade(stade.id, 'kc', parseFloat(e.target.value))}
                    className="w-full border rounded px-2 py-1 text-sm disabled:bg-gray-200" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">Hum Min %</label>
                  <input type="number" value={stade.seuil_humidite_min || ''} disabled={isLocked}
                    onChange={(e) => handleUpdateStade(stade.id, 'seuil_humidite_min', parseInt(e.target.value))}
                    className="w-full border rounded px-2 py-1 text-sm disabled:bg-gray-200" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">Hum Opt %</label>
                  <input type="number" value={stade.seuil_humidite_opt || ''} disabled={isLocked}
                    onChange={(e) => handleUpdateStade(stade.id, 'seuil_humidite_opt', parseInt(e.target.value))}
                    className="w-full border rounded px-2 py-1 text-sm disabled:bg-gray-200" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">Hum Max %</label>
                  <input type="number" value={stade.seuil_humidite_max || ''} disabled={isLocked}
                    onChange={(e) => handleUpdateStade(stade.id, 'seuil_humidite_max', parseInt(e.target.value))}
                    className="w-full border rounded px-2 py-1 text-sm disabled:bg-gray-200" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">T° Sol Max</label>
                  <input type="number" value={stade.seuil_temp_sol_max || ''} disabled={isLocked}
                    onChange={(e) => handleUpdateStade(stade.id, 'seuil_temp_sol_max', parseInt(e.target.value))}
                    className="w-full border rounded px-2 py-1 text-sm disabled:bg-gray-200" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500">Sensibilité</label>
                  <select value={stade.sensibilite_stress || 'moyenne'} disabled={isLocked}
                    onChange={(e) => handleUpdateStade(stade.id, 'sensibilite_stress', e.target.value)}
                    className="w-full border rounded px-2 py-1 text-sm disabled:bg-gray-200">
                    <option value="faible">Faible</option>
                    <option value="moyenne">Moyenne</option>
                    <option value="haute">Haute</option>
                    <option value="critique">Critique</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const FichesCulturales = () => {
  const [fiches, setFiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFiche, setSelectedFiche] = useState(null);
  const [modalMode, setModalMode] = useState(null); // 'create', 'edit', 'view'
  const [toast, setToast] = useState(null);
  const [filters, setFilters] = useState({ statut: '', type: '' });

  // Charger les fiches
  const loadFiches = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.statut) params.append('statut', filters.statut);
      if (filters.type) params.append('type', filters.type);
      
      const result = await api.get(`?${params}`);
      if (result.success) {
        setFiches(result.data);
      }
    } catch (error) {
      showToast('Erreur chargement', 'error');
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    loadFiches();
  }, [loadFiches]);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  // Actions CRUD
  const handleCreate = async (data) => {
    const result = await api.post('', { ...data, created_by: 'admin' });
    if (result.success) {
      showToast('Fiche créée !', 'success');
      setModalMode(null);
      loadFiches();
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleUpdate = async (data) => {
    const result = await api.put(`/${selectedFiche.id}`, data);
    if (result.success) {
      showToast('Fiche modifiée !', 'success');
      setModalMode(null);
      loadFiches();
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette fiche ?')) return;
    const result = await api.delete(`/${id}`);
    if (result.success) {
      showToast('Fiche supprimée', 'success');
      loadFiches();
    } else {
      showToast(result.error, 'error');
    }
  };

  // Actions Workflow
  const handleValider = async (id) => {
    const result = await api.post(`/${id}/valider`, { utilisateur: 'admin' });
    if (result.success) {
      showToast('✅ Fiche validée !', 'success');
      loadFiches();
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleProduction = async (id) => {
    if (!confirm('⚠️ Mettre en production ? La fiche sera VERROUILLÉE.')) return;
    const result = await api.post(`/${id}/production`, { utilisateur: 'admin' });
    if (result.success) {
      showToast('🚀 Fiche en production !', 'success');
      loadFiches();
    } else {
      showToast(result.error, 'error');
    }
  };

  const handleDupliquer = async (id) => {
    const result = await api.post(`/${id}/dupliquer`, { utilisateur: 'admin' });
    if (result.success) {
      showToast(`📋 Nouvelle version créée (ID: ${result.data.nouvelle_fiche_id})`, 'success');
      loadFiches();
    } else {
      showToast(result.error, 'error');
    }
  };

  // Ouvrir fiche détaillée
  const openFiche = async (id, mode = 'view') => {
    const result = await api.get(`/${id}`);
    if (result.success) {
      setSelectedFiche(result.data);
      setModalMode(mode);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          🌱 Fiches Culturales
          <span className="text-sm font-normal text-gray-500">Gestion des cultures IoT</span>
        </h1>
      </div>

      {/* Filtres + Actions */}
      <div className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex gap-3">
          <select
            value={filters.statut}
            onChange={(e) => setFilters(f => ({ ...f, statut: e.target.value }))}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Tous statuts</option>
            <option value="brouillon">📝 Brouillons</option>
            <option value="valide">✅ Validés</option>
            <option value="production">🚀 Production</option>
          </select>
          <select
            value={filters.type}
            onChange={(e) => setFilters(f => ({ ...f, type: e.target.value }))}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">Tous types</option>
            {TYPES_CULTURE.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => { setSelectedFiche(null); setModalMode('create'); }}
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
        >
          ➕ Nouvelle fiche
        </button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Chargement...</div>
      ) : fiches.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow">
          <div className="text-4xl mb-2">🌱</div>
          <div className="text-gray-500">Aucune fiche culturale</div>
          <button
            onClick={() => setModalMode('create')}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg"
          >
            Créer la première fiche
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {fiches.map(fiche => (
            <div
              key={fiche.id}
              className="bg-white rounded-xl shadow hover:shadow-lg transition-shadow p-4"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                    style={{ backgroundColor: fiche.couleur + '30' }}
                  >
                    {fiche.icone}
                  </div>
                  <div>
                    <div className="font-semibold">{fiche.nom}</div>
                    <div className="text-xs text-gray-500">{fiche.code}</div>
                  </div>
                </div>
                <StatutBadge statut={fiche.statut} />
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                <span>{TYPES_CULTURE.find(t => t.value === fiche.type_culture)?.label}</span>
                <span>•</span>
                <span>{fiche.jours_cycle_type}j</span>
                <span>•</span>
                <span>{fiche.nb_stades} stades</span>
              </div>

              <div className="flex items-center gap-2 pt-3 border-t">
                <button
                  onClick={() => openFiche(fiche.id, 'view')}
                  className="flex-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                >
                  👁️ Voir
                </button>
                {fiche.statut !== 'production' && (
                  <button
                    onClick={() => openFiche(fiche.id, 'edit')}
                    className="flex-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                  >
                    ✏️ Modifier
                  </button>
                )}
                
                {/* Actions workflow */}
                {fiche.statut === 'brouillon' && (
                  <>
                    <button onClick={() => handleValider(fiche.id)} className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200">
                      ✅
                    </button>
                    <button onClick={() => handleDelete(fiche.id)} className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200">
                      🗑️
                    </button>
                  </>
                )}
                {fiche.statut === 'valide' && (
                  <button onClick={() => handleProduction(fiche.id)} className="px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">
                    🚀
                  </button>
                )}
                {fiche.statut === 'production' && (
                  <button onClick={() => handleDupliquer(fiche.id)} className="px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200">
                    📋
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Création/Édition */}
      <Modal
        isOpen={modalMode === 'create' || modalMode === 'edit'}
        onClose={() => setModalMode(null)}
        title={modalMode === 'create' ? '➕ Nouvelle fiche culturale' : `✏️ Modifier: ${selectedFiche?.nom}`}
        size="lg"
      >
        <FicheForm
          fiche={selectedFiche}
          onSave={modalMode === 'create' ? handleCreate : handleUpdate}
          onCancel={() => setModalMode(null)}
        />
      </Modal>

      {/* Modal Vue détaillée */}
      <Modal
        isOpen={modalMode === 'view'}
        onClose={() => setModalMode(null)}
        title={`${selectedFiche?.icone} ${selectedFiche?.nom}`}
        size="xl"
      >
        {selectedFiche && (
          <div className="space-y-6">
            {/* Infos générales */}
            <div className="flex items-center gap-4">
              <StatutBadge statut={selectedFiche.statut} />
              <span className="text-sm text-gray-500">{selectedFiche.type_culture}</span>
              <span className="text-sm text-gray-500">•</span>
              <span className="text-sm text-gray-500">{selectedFiche.jours_cycle_type} jours</span>
            </div>

            {/* Stades */}
            <StadesEditor
              ficheId={selectedFiche.id}
              stades={selectedFiche.stades}
              isLocked={selectedFiche.statut === 'production'}
              onUpdate={loadFiches}
            />

            {/* Actionneurs */}
            <div>
              <h3 className="font-medium mb-3">⚡ Actionneurs configurés</h3>
              {selectedFiche.actionneurs?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedFiche.actionneurs.map(a => (
                    <span key={a.id} className="px-3 py-1 bg-gray-100 rounded-full text-sm flex items-center gap-1">
                      {a.icone} {a.nom}
                      {a.flag_declencheur && <span className="text-xs text-gray-500">→ {a.flag_declencheur}</span>}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 text-sm">Aucun actionneur configuré</div>
              )}
            </div>

            {/* Flags */}
            <div>
              <h3 className="font-medium mb-3">🚩 FLAGS activés</h3>
              {selectedFiche.flags?.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {selectedFiche.flags.map(f => {
                    const def = FLAGS_DISPONIBLES.find(fd => fd.code === f.flag_code);
                    return (
                      <span key={f.id} className={`px-3 py-1 rounded-full text-sm ${f.actif ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
                        {def?.icone} {f.flag_code}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div className="text-gray-500 text-sm">Aucun flag configuré</div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FichesCulturales;